import { useEffect, useMemo, useState } from "react"
import { Button } from "@aix/ui"

import { trpc } from "../../lib/trpc"

type EvalStatus = "PASS" | "FAIL"

interface EvalRunSummary {
  id: string
  createdAt: Date
  suiteId: string
  product: string
  suiteName: string
  source: string
  status: EvalStatus
  totalTests: number
  passedTests: number
  failedTests: number
  skippedTests: number
  agentId?: number | null
  agentName?: string | null
  targetRepoBranch?: string | null
  targetRepoSha?: string | null
}

interface EvalRunTest {
  id: string
  name: string
  label?: string | null
  status: EvalStatus
  sideEffect?: string | null
  agentRunId?: number | null
  output?: string | null
  checks?: Array<{ label: string; ok: boolean; detail?: string }> | null
}

interface EvalRunLog {
  id: string
  createdAt: Date
  level: string
  message: string
}

function formatDate(value?: Date | string | null): string {
  if (!value) return ""
  return new Date(value).toLocaleString()
}

function statusClass(status: EvalStatus): string {
  return status === "PASS"
    ? "bg-primary/10 text-primary border-primary/30"
    : "bg-destructive/10 text-destructive border-destructive/30"
}

function shortSha(value?: string | null): string {
  return value ? value.slice(0, 8) : ""
}

export default function EvalsScreen() {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const runsQuery = trpc.evals.listRuns.useQuery({ product: "aix-auto-agents", limit: 100 })
  const selectedRunQuery = trpc.evals.getRun.useQuery(
    { id: selectedRunId ?? "" },
    { enabled: selectedRunId != null },
  )

  const runs = (runsQuery.data ?? []) as EvalRunSummary[]
  const selectedRun = selectedRunQuery.data?.run as EvalRunSummary | undefined
  const selectedTests = (selectedRunQuery.data?.tests ?? []) as EvalRunTest[]
  const selectedLogs = (selectedRunQuery.data?.logs ?? []) as EvalRunLog[]

  const defaultSelectedId = useMemo(() => selectedRunId ?? runs[0]?.id ?? null, [runs, selectedRunId])

  useEffect(() => {
    if (defaultSelectedId && !selectedRunId) setSelectedRunId(defaultSelectedId)
  }, [defaultSelectedId, selectedRunId])

  return (
    <div className="h-full flex flex-col bg-background">
      <header className="border-b border-border bg-secondary px-6 py-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Eval runs</h1>
          <p className="text-sm text-muted-foreground mt-1">Capability suite history, scores, and logs.</p>
        </div>
        <Button variant="outline" onClick={() => runsQuery.refetch()}>
          Refresh
        </Button>
      </header>

      <main className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[420px_1fr]">
        <section className="border-r border-border overflow-y-auto">
          {runsQuery.isLoading && <div className="p-6 text-sm text-muted-foreground">Loading eval runs...</div>}
          {!runsQuery.isLoading && runs.length === 0 && <div className="p-6 text-sm text-muted-foreground">No eval runs yet.</div>}
          <div className="divide-y divide-border">
            {runs.map((run) => (
              <button
                key={run.id}
                type="button"
                onClick={() => setSelectedRunId(run.id)}
                className={`w-full text-left p-4 hover:bg-secondary transition-colors ${
                  selectedRunId === run.id ? "bg-secondary" : "bg-background"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-foreground truncate">{run.suiteName}</span>
                  <span className={`border px-2 py-0.5 text-xs font-bold ${statusClass(run.status)}`}>{run.status}</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                  <span>{formatDate(run.createdAt)}</span>
                  <span>{run.passedTests}/{run.totalTests} passed</span>
                  {run.skippedTests > 0 && <span>{run.skippedTests} skipped</span>}
                </div>
                <div className="mt-2 text-xs text-muted-foreground truncate">
                  {run.agentName || (run.agentId ? `agent #${run.agentId}` : run.source)}
                  {run.targetRepoBranch ? ` · ${run.targetRepoBranch}` : ""}
                  {run.targetRepoSha ? ` · ${shortSha(run.targetRepoSha)}` : ""}
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="overflow-y-auto p-6">
          {!selectedRunId && <div className="text-sm text-muted-foreground">Select an eval run.</div>}
          {selectedRunQuery.isLoading && <div className="text-sm text-muted-foreground">Loading run detail...</div>}
          {selectedRun && (
            <div className="space-y-6 max-w-5xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-foreground">{selectedRun.suiteId}</h2>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {selectedRun.product} · {selectedRun.source} · {formatDate(selectedRun.createdAt)}
                  </div>
                </div>
                <span className={`border px-3 py-1 text-sm font-bold ${statusClass(selectedRun.status)}`}>
                  {selectedRun.status}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Total" value={String(selectedRun.totalTests)} />
                <Metric label="Passed" value={String(selectedRun.passedTests)} />
                <Metric label="Failed" value={String(selectedRun.failedTests)} />
                <Metric label="Skipped" value={String(selectedRun.skippedTests)} />
              </div>

              <div>
                <h3 className="text-sm font-bold text-foreground mb-2">Tests</h3>
                <div className="border border-border divide-y divide-border">
                  {selectedTests.map((test) => (
                    <div key={test.id} className="p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-foreground">{test.label || test.name}</div>
                        <span className={`border px-2 py-0.5 text-xs font-bold ${statusClass(test.status)}`}>
                          {test.status}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {test.name}
                        {test.sideEffect ? ` · ${test.sideEffect}` : ""}
                        {test.agentRunId ? ` · agent run #${test.agentRunId}` : ""}
                      </div>
                      {test.checks && test.checks.length > 0 && (
                        <div className="mt-3 space-y-1">
                          {test.checks.map((check) => (
                            <div key={`${test.id}-${check.label}`} className="text-xs text-muted-foreground">
                              <span className={check.ok ? "text-primary" : "text-destructive"}>
                                {check.ok ? "PASS" : "FAIL"}
                              </span>{" "}
                              {check.label}
                              {check.detail ? `: ${check.detail}` : ""}
                            </div>
                          ))}
                        </div>
                      )}
                      {test.output && (
                        <pre className="mt-3 max-h-72 overflow-auto bg-muted p-3 text-xs whitespace-pre-wrap text-foreground">
                          {test.output}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-foreground mb-2">Logs</h3>
                <div className="border border-border divide-y divide-border">
                  {selectedLogs.length === 0 && <div className="p-3 text-sm text-muted-foreground">No logs recorded.</div>}
                  {selectedLogs.map((log) => (
                    <div key={log.id} className="p-3 text-sm">
                      <span className="font-mono text-xs text-muted-foreground">{formatDate(log.createdAt)}</span>{" "}
                      <span className="font-bold uppercase text-foreground">{log.level}</span>{" "}
                      <span className="text-foreground">{log.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border bg-background p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold text-foreground">{value}</div>
    </div>
  )
}
